export function resolveInitialRoute(guards, fallback, ctx) {
    for (let i = 0; i < guards.length; i++) {
        const forced = guards[i](ctx);
        if (forced !== undefined)
            return { route: forced, forcedByGuard: i };
    }
    return { route: fallback };
}
/**
 * Where a web console goes once its boot resolved (D854 ADMIN-SHELL): the page the person asked for is KEPT.
 *
 * `bootApp` answers with the app's home (signed in) or its sign-in route (signed out), which is right for a first
 * visit but wrong for a reload or a link: it dropped the address (`/rides/grid`, `/cases`, `/recovery`) and its query.
 * Returns the route to navigate to, or `undefined` to stay exactly where the address already is.
 *
 * - A route a guard forced (a required update, an account gate, the sign-in of an anonymous visit to a private
 *   screen) wins.
 * - Signed in: any private screen stays; a public one (sign-in, recovery, session expired) goes to the home.
 * - Signed out: a public screen stays (a reset e-mail link, a bookmark of the recovery page); a private one goes to
 *   the sign-in the guard named.
 */
export function resolveBootRoute(input) {
    const { bootRoute, home, current, authenticated, publicRoutes } = input;
    if (authenticated) {
        if (bootRoute !== home)
            return bootRoute === current ? undefined : bootRoute;
        return publicRoutes.has(current) ? home : undefined;
    }
    if (publicRoutes.has(current) && (bootRoute === current || publicRoutes.has(bootRoute)))
        return undefined;
    return bootRoute === current ? undefined : bootRoute;
}
// ---- Standard guard factories --------------------------------------------------------------------
/** A required app update blocks everything else. */
export function forcedUpdateGuard(route = 'update_required') {
    return (ctx) => (ctx.update === 'required' ? route : undefined);
}
/** An anonymous session is sent to the app's entry/auth route. */
export function authGuard(anonymousRoute) {
    return (ctx) => (ctx.session.status === 'anonymous' ? anonymousRoute : undefined);
}
/** An app-resolved account gate (pending KYC, blocked account, …) wins over the default home. */
export function accountGuard() {
    return (ctx) => ctx.accountRoute;
}
/**
 * Whether `name` is reachable in `ctx`. Deny-by-default: an unknown route is `unknown` (never silently
 * allowed), an auth route while anonymous is `requires_auth` (the app redirects to sign-in and keeps the
 * intended route), and a realm mismatch is `forbidden_realm` — a consumer session can never open an operator
 * route and vice-versa.
 */
export function checkRouteAccess(graph, name, ctx) {
    const def = graph.routes[name];
    if (def === undefined)
        return { status: 'unknown' };
    if (def.requiresAuth && ctx.session.status !== 'authenticated')
        return { status: 'requires_auth' };
    if (def.requiresRealm !== undefined) {
        const realm = ctx.session.principal?.realm;
        if (realm === undefined || !def.requiresRealm.includes(realm)) {
            return { status: 'forbidden_realm', need: def.requiresRealm };
        }
    }
    return { status: 'ok' };
}
//# sourceMappingURL=navigation.js.map